import { useDashboardData } from "../../../context/DashboardContext";
import { MetricCard } from "../../MetricCard";
import { AdditionalStatCard, StatCard } from "../../StatCard";
import type { WidgetComponentProps } from "../types";
import {
  computeCustomMetricValue,
  formatMetricValue,
  type CustomMetricConfig,
  type CustomMetricFormat,
  type CustomMetricVariant,
  type MetricColorTheme,
} from "../customMetricUtils";

const icons: Record<string, React.ReactNode> = {
  dollar: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
  trending: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>,
  target: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>,
  chart: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>,
  users: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
  wallet: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>,
  percent: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>,
};

export default function CustomMetricWidget({ config, title }: WidgetComponentProps) {
  const { metrics } = useDashboardData();
  const widgetConfig = config as unknown as CustomMetricConfig;
  const variant = (widgetConfig.variant ?? "metric") as CustomMetricVariant;
  const format = (widgetConfig.format ?? "number") as CustomMetricFormat;
  const color = (widgetConfig.color ?? "brand") as MetricColorTheme;
  const icon = widgetConfig.icon ? icons[widgetConfig.icon] : undefined;
  const value = computeCustomMetricValue(metrics, widgetConfig);
  const formattedValue = formatMetricValue(value, format);

  if (variant === "stat") {
    return <StatCard label={title} value={formattedValue} index={0} />;
  }

  if (variant === "additional") {
    return (
      <AdditionalStatCard
        label={title}
        value={formattedValue}
        description={widgetConfig.description}
        index={0}
      />
    );
  }

  return (
    <MetricCard
      label={title}
      value={formattedValue}
      icon={icon}
      color={color}
      data={[]}
      index={0}
    />
  );
}
